/**
 * FC Famalicão — lightbox: modal adaptável, fechar fora/X, sem view transitions.
 */
import { debounce, isClickedOutside } from '@theme/utilities';

const MAX_IMAGE_WIDTH = 1120;
const MIN_IMAGE_WIDTH = 240;
const MIN_IMAGE_HEIGHT = 240;

/** @param {HTMLDialogElement} dialog */
function readSideWidth(dialog) {
  const styles = getComputedStyle(dialog);
  const thumb = parseFloat(styles.getPropertyValue('--fcf-zoom-thumb-width')) || 72;
  const gutter = parseFloat(styles.getPropertyValue('--fcf-zoom-thumb-gutter')) || 32;
  return thumb + gutter;
}

/** @param {HTMLDialogElement} dialog */
function readMaxImageSize(dialog) {
  const side = readSideWidth(dialog);
  const viewportPad = window.innerWidth <= 749 ? 24 : 48;
  const maxWidth = Math.min(MAX_IMAGE_WIDTH, window.innerWidth - viewportPad - side);
  const maxHeight = window.innerHeight * (window.innerWidth <= 749 ? 0.92 : 0.88);
  return { maxWidth: Math.max(maxWidth, MIN_IMAGE_WIDTH), maxHeight: Math.max(maxHeight, MIN_IMAGE_HEIGHT) };
}

/** @param {number} width @param {number} height @param {{ maxWidth: number, maxHeight: number }} limits */
function scaleToFit(width, height, limits) {
  if (!width || !height) return null;
  const scale = Math.min(limits.maxWidth / width, limits.maxHeight / height, 1);
  return {
    width: Math.max(Math.round(width * scale), MIN_IMAGE_WIDTH),
    height: Math.max(Math.round(height * scale), MIN_IMAGE_HEIGHT),
  };
}

/** @param {Element} zoomDialog @param {number} index */
function fitDialogToImage(zoomDialog, index) {
  const dialog = zoomDialog.querySelector('dialog.dialog-zoomed-gallery__dialog');
  if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

  /** @type {HTMLElement[] | undefined} */
  const media = /** @type {{ refs?: { media?: HTMLElement[] } }} */ (zoomDialog).refs?.media;
  const container = media?.[index] ?? dialog.querySelectorAll('.dialog-zoomed-gallery .product-media-container')[index];
  const img = container?.querySelector('img.product-media__image');
  if (!(img instanceof HTMLImageElement)) return;

  const apply = () => {
    const limits = readMaxImageSize(dialog);
    const fitted = scaleToFit(img.naturalWidth, img.naturalHeight, limits);
    if (!fitted) return;

    dialog.style.setProperty('--fcf-zoom-image-width', `${fitted.width}px`);
    dialog.style.setProperty('--fcf-zoom-image-height', `${fitted.height}px`);
  };

  if (img.complete && img.naturalWidth > 0) {
    apply();
  } else {
    img.addEventListener('load', apply, { once: true });
  }
}

/** @param {Element} zoomDialog */
function getActiveIndex(zoomDialog) {
  const selected = zoomDialog.querySelector('.dialog-thumbnails-list__thumbnail[aria-selected="true"]');
  if (selected?.parentElement) {
    return Array.from(selected.parentElement.children).indexOf(selected);
  }

  /** @type {HTMLElement[] | undefined} */
  const media = /** @type {{ refs?: { media?: HTMLElement[] } }} */ (zoomDialog).refs?.media;
  return media?.length ? 0 : -1;
}

/** @param {Element} zoomDialog */
function fitDialogToActiveImage(zoomDialog) {
  const index = getActiveIndex(zoomDialog);
  if (index >= 0) fitDialogToImage(zoomDialog, index);
}

/** Desactiva view transitions do Horizon (causam "piscar" com o modal customizado). */
function patchZoomDialogTransitions() {
  const ZoomDialogCtor = customElements.get('zoom-dialog');
  if (!ZoomDialogCtor || ZoomDialogCtor.prototype._fcfInstantZoom) return;

  const proto = ZoomDialogCtor.prototype;
  proto._fcfInstantZoom = true;

  const originalSelect = proto.selectThumbnail;

  proto.selectThumbnail = async function selectThumbnailWithFit(index, options) {
    await originalSelect.call(this, index, options);
    fitDialogToImage(this, index);
  };

  proto.open = async function openInstant(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    if (!dialog.open) {
      dialog.showModal();
    }

    for (const target of [targetThumbnail, targetImage]) {
      target?.scrollIntoView({ behavior: 'instant' });
    }

    await this.selectThumbnail(index, { behavior: 'instant' });
    fitDialogToImage(this, index);
  };

  proto.close = async function closeInstant() {
    const { dialog } = this.refs;

    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    dialog.style.removeProperty('--fcf-zoom-image-width');
    dialog.style.removeProperty('--fcf-zoom-image-height');

    this.querySelectorAll('[style*="view-transition-name"]').forEach((element) => {
      if (element instanceof HTMLElement) {
        element.style.removeProperty('view-transition-name');
      }
    });

    this.closeDialog();
  };
}

/** @param {Element} zoomDialog */
function bindZoomDialogClose(zoomDialog) {
  if (!(zoomDialog instanceof HTMLElement) || zoomDialog.dataset.fcfZoomCloseBound === 'true') return;

  const dialog = zoomDialog.querySelector('dialog.dialog-zoomed-gallery__dialog');
  if (!(dialog instanceof HTMLDialogElement)) return;

  zoomDialog.dataset.fcfZoomCloseBound = 'true';

  /** @param {Event} [event] */
  const close = (event) => {
    if (!dialog.open) return;
    event?.preventDefault();
    event?.stopPropagation();

    if (typeof zoomDialog.close === 'function') {
      zoomDialog.close();
    } else {
      dialog.close();
    }
  };

  dialog.addEventListener('click', (event) => {
    if (!dialog.open) return;
    if (isClickedOutside(event, dialog)) {
      close(event);
    }
  });

  dialog.querySelector('.dialog-zoomed-gallery__close-button')?.addEventListener('click', close);

  const refit = debounce(() => fitDialogToActiveImage(zoomDialog), 80);
  dialog.addEventListener('scroll', refit, { passive: true });
  window.addEventListener('resize', refit);

  dialog.querySelectorAll('.dialog-zoomed-gallery img.product-media__image').forEach((img) => {
    if (img instanceof HTMLImageElement && !img.complete) {
      img.addEventListener('load', refit, { once: true });
    }
  });
}

function initZoomDialogClose() {
  patchZoomDialogTransitions();
  document.querySelectorAll('zoom-dialog').forEach((zoomDialog) => bindZoomDialogClose(zoomDialog));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initZoomDialogClose, { once: true });
} else {
  initZoomDialogClose();
}

document.addEventListener('shopify:section:load', initZoomDialogClose);

const observer = new MutationObserver(initZoomDialogClose);
observer.observe(document.documentElement, { childList: true, subtree: true });
